import type { CourseQuizContent } from '../types'

export const navySealsContent: CourseQuizContent = {
  courseSlug: 'leadership-and-decision-making-under-pressure',
  courseName: 'Leadership and Decision-Making Under Pressure',

  goalQuestion: {
    title: 'How would you describe your leadership role today?',
    options: [
      {
        label: 'I lead without the formal title. Projects, peers, influence.',
        value: 'no-title',
      },
      { label: 'I run a team day to day.', value: 'run-a-team' },
      {
        label:
          'I lead other leaders: managers, departments, or a whole company.',
        value: 'lead-leaders',
      },
    ],
  },

  levelQuestion: {
    title: 'What’s your biggest leadership challenge right now?',
    options: [
      {
        label:
          'Making better calls under pressure, deciding fast, without regret.',
        value: 'better-calls',
      },
      {
        label: 'Getting my team executing without me in every detail.',
        value: 'team-executing',
      },
      {
        label: 'Turning strategy into momentum, plans that actually get done.',
        value: 'strategy-momentum',
      },
      {
        label: 'Stepping up in scope and growing into bigger leadership roles.',
        value: 'step-up',
      },
    ],
  },

  primaryQuestionSlot: 'level',

  interstitial1: {
    pill: 'IN THIS COURSE',
    headline:
      'You’ll learn from a Medal of Honor recipient and leave with your own 90-Day Leadership Blueprint.',
    body: 'Learn go/no-go criteria and the 3-Step AAR debrief, then apply them to real business scenarios in every module.',
    image: 'https://static.masterclass.com/nsf-interstitial-1.png',
    imageAlt: 'The 90-Day Leadership Blueprint course materials',
  },

  interstitial2: {
    pill: 'DID YOU KNOW',
    headline: 'Elite standards at a human pace.',
    body: 'Learn the SEAL frameworks on your own schedule. The course is fully self-paced, with no deadlines or prerequisites.',
    image: 'https://static.masterclass.com/nsf-interstitial-2.png',
    imageAlt: 'A learner taking the course at their own pace',
  },

  profiles: [
    {
      key: 'better-calls',
      headline:
        'When you’re under pressure, you want to be able to make decisions you won’t regret',
      lead: 'You find yourself often having to make hard decisions with incomplete information, and little time to contemplate. Composure in high-pressure moments can be learned, and this course teaches you the trainable system elite teams rely on.',
      bullets: [
        {
          text: 'Build the **“chair flying” rehearsal** SEALs use: walk through the hard moment before it happens.',
        },
        {
          text: '**Design go/no-go criteria** for a live project, so risky decisions stop being gut calls.',
        },
        {
          text: 'Create a **decision review routine** that turns every outcome into better judgment next time.',
        },
      ],
    },
    {
      key: 'team-executing',
      headline: 'You want a team that executes, without you in every detail',
      lead: 'If everything still routes through you, you don’t have a team. You have a queue. This course gives you the ownership and debrief systems that empower your team to run without you in the room.',
      bullets: [
        {
          text: '**Map who decides what** with a RACI chart to address the decisions slowing everything down.',
        },
        {
          text: '**Develop the mid-level leaders** who touch the most parts of your organization.',
        },
        {
          text: 'Run the **3-Step AAR**, the SEAL debrief method that keeps feedback honest and routine, whatever the rank.',
        },
      ],
    },
    {
      key: 'strategy-momentum',
      headline: 'You want to turn strategy into momentum',
      lead: 'Your most strategic plans tend to fall apart in execution. This course gives you the planning and communication systems that ensure your vision translates to action items your team can follow.',
      bullets: [
        {
          text: '**Design your own planning system** from the SEAL toolset, tailored to your leadership challenges.',
        },
        {
          text: '**Break complex goals into clear next steps with timelines and assigned roles** your team can act on.',
        },
        {
          text: '**Plan information flow** deliberately, so the right people know what they need, when they need it.',
        },
      ],
    },
    {
      key: 'step-up',
      headline: 'You want to step up in scope, and prove you’re ready',
      lead: 'You’re ready for more scope, and the next role goes to whoever can demonstrate they’re ready as a leader. This course helps you build that proof.',
      bullets: [
        {
          text: '**Assess yourself across six leadership dimensions**, then measure your growth at the end of the course.',
        },
        {
          text: 'Learn the **C3+ model** of Courage, Compassion, and Communication, which elite teams use for high-stakes decisions.',
        },
        {
          text: 'Finish with a **90-Day Leadership Blueprint** that shows you can think ahead, move fast, and adapt.',
        },
      ],
    },
  ],

  paceNotes: [
    { key: 'lt1h', weeks: '16 to 20 weeks' },
    { key: '1to3h', weeks: '6 to 10 weeks' },
    { key: '3to5h', weeks: '4 to 5 weeks' },
    { key: '5plush', weeks: '3 weeks' },
  ],

  previewLessons: [
    {
      title: 'Intro to High-Stakes Leadership',
      mediaUuid: '129d2fe9-0ad8-4e16-aec5-dfbd6276e032',
      image: 'https://static.masterclass.com/nsf-video-1.png',
    },
    {
      title: 'Strategic Readiness Starts With You',
      mediaUuid: 'a455c75c-5ff4-40c7-9555-f700212fe6a8',
      image: 'https://static.masterclass.com/nsf-video-2.png',
    },
  ],

  ctaHighlights: [
    'Taught by former Navy SEALs.',
    'Create your 90-Day Leadership Blueprint.',
    'Earn a shareable MasterClass Certificate.',
  ],
}
